import { useCallback, useRef } from "react"
import { useReactFlow } from "@xyflow/react"
import { NODE_LABELS } from "../constants"

/**
 * Manages node CRUD operations: add, update, delete, label change, and double-click drill-down.
 */
export default function useNodeOperations(flowSlug, nodes, setNodes, setEdges, selectedNode, setSelectedNode, setSelectedEdge) {
  const reactFlowInstance = useReactFlow()
  const nodeIdCounter = useRef(nodes.length)

  // Generate unique node ID
  const generateNodeId = useCallback(() => {
    nodeIdCounter.current += 1
    return `node_${Date.now()}_${nodeIdCounter.current}`
  }, [])

  // Add node handler (used by Stimulus drag-and-drop)
  const handleAddNode = useCallback((event) => {
    const { type, position } = event.detail
    if (!type || !position) return

    const flowPosition = reactFlowInstance.screenToFlowPosition(position)

    const newNode = {
      id: generateNodeId(),
      type,
      position: flowPosition,
      data: {
        label: NODE_LABELS[type] || "New Node",
      },
    }

    setNodes((nds) => [...nds, newNode])
  }, [reactFlowInstance, generateNodeId, setNodes])

  // Update node data
  const handleNodeUpdate = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === nodeId) {
          return {
            ...n,
            data: { ...n.data, ...newData.data },
          }
        }
        return n
      })
    )
    setSelectedNode((current) => {
      if (current?.id === nodeId) {
        return { ...current, data: { ...current.data, ...newData.data } }
      }
      return current
    })
  }, [setNodes, setSelectedNode])

  // Delete node and its connected edges
  const handleNodeDelete = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId))
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
    setSelectedNode(null)
  }, [setNodes, setEdges, setSelectedNode])

  // Inline label editing with server persistence
  const handleLabelChange = useCallback(async (nodeId, newLabel) => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === nodeId) {
          return {
            ...n,
            data: { ...n.data, label: newLabel },
          }
        }
        return n
      })
    )

    setSelectedNode((current) => {
      if (current?.id === nodeId) {
        return { ...current, data: { ...current.data, label: newLabel } }
      }
      return current
    })

    try {
      await fetch(`/flows/${flowSlug}/nodes/${nodeId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ node: { label: newLabel } }),
      })
    } catch (error) {
      console.error("Failed to save label:", error)
    }
  }, [flowSlug, setNodes, setSelectedNode])

  // Node click handler (with drag guard)
  const isDraggingRef = useRef(false)

  const handleNodeDragStart = useCallback(() => {
    isDraggingRef.current = true
  }, [])

  const handleNodeDragStop = useCallback(() => {
    setTimeout(() => {
      isDraggingRef.current = false
    }, 0)
  }, [])

  const handleNodeClick = useCallback(
    (event, node) => {
      if (isDraggingRef.current) return
      setSelectedNode(node)
      setSelectedEdge(null)
    },
    [setSelectedNode, setSelectedEdge]
  )

  // Double-click drill-down
  const handleNodeDoubleClick = useCallback(
    (event, node) => {
      if (node.data.drillDownSlug) {
        window.Turbo.visit(`/flows/${node.data.drillDownSlug}`)
      }
    },
    []
  )

  const handlePanelClose = useCallback(() => {
    setSelectedNode(null)
  }, [setSelectedNode])

  return {
    handleAddNode,
    handleNodeUpdate,
    handleNodeDelete,
    handleLabelChange,
    handleNodeDragStart,
    handleNodeDragStop,
    handleNodeClick,
    handleNodeDoubleClick,
    handlePanelClose,
  }
}
